import * as Sentry from '@sentry/react'
import { apiClient } from '@/shared/api/apiClient'

const FAILED_STATUS_RETRIES = 3
const FAILED_STATUS_RETRY_DELAY_MS = 400

const inFlightUploads = new Map<string, { productId: string }>()
const inFlightListeners = new Set<() => void>()
const emptyInFlightIds: string[] = []
const inFlightSnapshotsByProductId = new Map<string, string[]>()
let pagehideRegistered = false

function arraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false
    }
  }
  return true
}

function buildInFlightSnapshot(productId: string): string[] {
  const sourceIds: string[] = []
  for (const [sourceId, entry] of inFlightUploads.entries()) {
    if (entry.productId === productId) {
      sourceIds.push(sourceId)
    }
  }
  return sourceIds
}

function notifyInFlightListeners(): void {
  for (const listener of inFlightListeners) {
    listener()
  }
}

export function subscribeInFlightProductSourceUploads(
  listener: () => void
): () => void {
  inFlightListeners.add(listener)
  return () => {
    inFlightListeners.delete(listener)
  }
}

export function getInFlightProductSourceUploadIdsForProduct(
  productId: string
): readonly string[] {
  const next = buildInFlightSnapshot(productId)
  if (next.length === 0) {
    inFlightSnapshotsByProductId.delete(productId)
    return emptyInFlightIds
  }

  const cached = inFlightSnapshotsByProductId.get(productId)
  if (cached != null && arraysEqual(cached, next)) {
    return cached
  }

  const snapshot = [...next]
  inFlightSnapshotsByProductId.set(productId, snapshot)
  return snapshot
}

function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? ''
}

function registerPagehideBestEffortFailed(): void {
  if (pagehideRegistered || typeof window === 'undefined') {
    return
  }
  pagehideRegistered = true
  window.addEventListener('pagehide', () => {
    const baseUrl = getApiBaseUrl()
    for (const [sourceId, { productId }] of inFlightUploads.entries()) {
      const url = `${baseUrl}/products/${productId}/sources/${sourceId}/upload-status`
      try {
        void fetch(url, {
          method: 'PATCH',
          credentials: 'include',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileUploadStatus: 'FAILED' }),
        })
      } catch {
        // Best-effort only.
      }
    }
  })
}

export function trackInFlightProductSourceUpload(
  productId: string,
  sourceId: string
): void {
  registerPagehideBestEffortFailed()
  inFlightUploads.set(sourceId, { productId })
  notifyInFlightListeners()
}

export function untrackInFlightProductSourceUpload(sourceId: string): void {
  if (!inFlightUploads.delete(sourceId)) {
    return
  }
  notifyInFlightListeners()
}

export async function persistProductSourceUploadFailed(
  productId: string,
  sourceId: string
): Promise<boolean> {
  let lastError: unknown
  for (let attempt = 0; attempt < FAILED_STATUS_RETRIES; attempt += 1) {
    try {
      await apiClient.patch(
        `/products/${productId}/sources/${sourceId}/upload-status`,
        { fileUploadStatus: 'FAILED' }
      )
      return true
    } catch (error) {
      lastError = error
      if (attempt < FAILED_STATUS_RETRIES - 1) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, FAILED_STATUS_RETRY_DELAY_MS)
        })
      }
    }
  }

  Sentry.captureException(
    lastError instanceof Error ? lastError : new Error('Upload FAILED persist failed'),
    {
      tags: {
        feature: 'product-source-upload',
        step: 'persist_upload_failed',
      },
      extra: { productId, sourceId },
    }
  )
  return false
}
